import { notifyError } from "@/lib/notify-error";
import { useCallback, useState, type FormEvent } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { AlertCircle, CheckCircle2, Edit3, Loader2, Trash2, Upload, Video, X } from "lucide-react";
import { useDropzone } from "react-dropzone";
import { toast } from "sonner";

import { useConfirm } from "@/components/confirm-provider";
import { PageHeader } from "@/components/layout";
import { StatusBadge, type StatusKey } from "@/components/status-badge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { formatFileSize } from "@/lib/formatting";
import { cn } from "@/lib/utils";

interface AdminTrainingVideo {
  _id: Id<"trainingVideos">;
  title: string;
  description: string | null;
  fileStorageKey: string;
  durationSeconds: number | null;
  status: "uploading" | "processing" | "ready" | "failed";
  errorMessage: string | null;
  isPublished: boolean;
  createdAt: number;
}

const MIN_UPLOAD_TIMEOUT_MS = 5 * 60_000;
const ESTIMATED_UPLOAD_BYTES_PER_MS = 256;

export const Route = createFileRoute("/admin/training")({
  component: AdminTrainingPage,
});

function formatDuration(seconds: number | null) {
  if (!seconds) return "-";
  const rounded = Math.round(seconds);
  const minutes = Math.floor(rounded / 60);
  const remainingSeconds = rounded % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
}

const VIDEO_STATUS: Record<AdminTrainingVideo["status"], { status: StatusKey; label: string }> = {
  uploading: { status: "processing", label: "Uploading" },
  processing: { status: "processing", label: "Processing" },
  ready: { status: "completed", label: "Ready" },
  failed: { status: "error", label: "Failed" },
};

function getUploadTimeoutMs(fileSizeBytes: number) {
  return Math.max(
    MIN_UPLOAD_TIMEOUT_MS,
    Math.ceil(fileSizeBytes / ESTIMATED_UPLOAD_BYTES_PER_MS)
  );
}

async function uploadR2File(args: {
  uploadUrl: string;
  file: File;
  onProgress: (progress: number) => void;
  signal?: AbortSignal;
  timeoutMs?: number;
}) {
  await new Promise<void>((resolve, reject) => {
    const request = new XMLHttpRequest();
    let settled = false;
    const cleanup = () => {
      args.signal?.removeEventListener("abort", handleSignalAbort);
    };
    const resolveOnce = () => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve();
    };
    const rejectOnce = (error: Error) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(error);
    };
    const handleSignalAbort = () => {
      request.abort();
      rejectOnce(new Error("R2 upload aborted"));
    };

    request.open("PUT", args.uploadUrl);
    request.timeout = args.timeoutMs ?? 120_000;
    request.setRequestHeader("Content-Type", args.file.type || "application/octet-stream");
    request.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        args.onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        args.onProgress(100);
        resolveOnce();
        return;
      }
      rejectOnce(new Error(`R2 upload failed with ${request.status}`));
    };
    request.onerror = () => rejectOnce(new Error("R2 upload failed"));
    request.onabort = () => rejectOnce(new Error("R2 upload aborted"));
    request.ontimeout = () => {
      request.abort();
      rejectOnce(new Error("R2 upload timed out"));
    };

    if (args.signal?.aborted) {
      handleSignalAbort();
      return;
    }
    args.signal?.addEventListener("abort", handleSignalAbort, { once: true });
    request.send(args.file);
  });
}

export function AdminTrainingPage() {
  const { data: videosData, isPending } = useQuery(
    convexQuery(api.trainingVideos.listForAdmin, {})
  );
  const createUpload = useMutation(api.trainingVideos.createUpload);
  const deleteVideo = useMutation(api.trainingVideos.deleteVideo);
  const markUploadSubmitted = useMutation(api.trainingVideos.markUploadSubmitted);
  const markUploadFailed = useMutation(api.trainingVideos.markUploadFailed);
  const updateMetadata = useMutation(api.trainingVideos.updateMetadata);
  const confirm = useConfirm();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isBusy, setIsBusy] = useState(false);

  const [editingVideo, setEditingVideo] = useState<AdminTrainingVideo | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editPublished, setEditPublished] = useState(false);

  const videos = (videosData ?? []) as AdminTrainingVideo[];
  const uploadDisabled = isUploading || !file || title.trim().length === 0;

  const onVideoDrop = useCallback((acceptedFiles: File[]) => {
    const selectedFile = acceptedFiles[0] ?? null;
    setFile(selectedFile);
    setFileError(null);
  }, []);

  const onVideoDropRejected = useCallback(() => {
    setFile(null);
    setFileError("Only MP4 and WebM video files are supported.");
  }, []);

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragReject,
  } = useDropzone({
    accept: {
      "video/mp4": [".mp4"],
      "video/webm": [".webm"],
    },
    disabled: isUploading,
    multiple: false,
    onDrop: onVideoDrop,
    onDropRejected: onVideoDropRejected,
  });

  const handleUpload = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    if (!file) {
      setFileError("Select an MP4 or WebM video before uploading.");
      toast.error("Select a video file to upload");
      return;
    }
    if (!title.trim()) {
      toast.error("Enter a title before uploading");
      return;
    }
    if (file.type && !["video/mp4", "video/webm"].includes(file.type)) {
      setFileError("Only MP4 and WebM video files are supported.");
      toast.error("Upload an MP4 or WebM video file");
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    let videoId: Id<"trainingVideos"> | null = null;
    let uploadFinalized = false;
    try {
      const upload = await createUpload({
        title,
        description,
        filename: file.name,
      });
      videoId = upload.videoId;
      await uploadR2File({
        uploadUrl: upload.uploadUrl,
        file,
        onProgress: setUploadProgress,
        timeoutMs: getUploadTimeoutMs(file.size),
      });
      await markUploadSubmitted({ videoId: upload.videoId });
      uploadFinalized = true;
      toast.success("Video uploaded and published.");
      setTitle("");
      setDescription("");
      setFile(null);
      setFileError(null);
      setUploadProgress(null);
      form.reset();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Video upload failed";
      if (videoId && !uploadFinalized) {
        try {
          await markUploadFailed({ videoId, errorMessage: message });
        } catch {
          // Keep the original upload error visible to the admin.
        }
      }
      notifyError(error, message);
    } finally {
      setIsUploading(false);
    }
  };

  const openEditDialog = (video: AdminTrainingVideo) => {
    setEditingVideo(video);
    setEditTitle(video.title);
    setEditDescription(video.description ?? "");
    setEditPublished(video.isPublished);
  };

  const closeEditDialog = () => {
    setEditingVideo(null);
    setEditTitle("");
    setEditDescription("");
    setEditPublished(false);
  };

  const handleSave = async () => {
    if (!editingVideo) return;
    const trimmedTitle = editTitle.trim();
    if (trimmedTitle.length === 0) {
      toast.error("Title is required");
      return;
    }
    setIsBusy(true);
    try {
      await updateMetadata({
        videoId: editingVideo._id,
        title: trimmedTitle,
        description: editDescription || undefined,
        isPublished: editPublished,
      });
      toast.success("Training video updated");
      closeEditDialog();
    } catch (error) {
      notifyError(error, error instanceof Error ? error.message : "Failed to update video");
    } finally {
      setIsBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!editingVideo) return;
    const confirmed = await confirm({
      title: "Delete training video?",
      description: `Delete "${editingVideo.title}" from R2 and Training? This cannot be undone.`,
      confirmLabel: "Delete",
      tone: "destructive",
    });
    if (!confirmed) {
      return;
    }
    setIsBusy(true);
    try {
      await deleteVideo({ videoId: editingVideo._id });
      toast.success("Training video deleted");
      closeEditDialog();
    } catch (error) {
      notifyError(error, error instanceof Error ? error.message : "Failed to delete video");
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Training Videos" />

      <Card className="overflow-hidden py-0">
        <CardHeader className="border-b bg-muted/30 py-4">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <Upload className="size-4" />
                Upload Video
              </CardTitle>
              <p className="mt-1 text-sm text-muted-foreground">
                Add a customer-facing training video. Videos publish automatically after upload.
              </p>
            </div>
            <Badge variant="outline" className="w-fit">
              MP4 or WebM
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="p-4 sm:p-6">
          <form className="grid gap-5 xl:grid-cols-[minmax(280px,0.85fr)_minmax(0,1fr)]" onSubmit={handleUpload}>
            <div className="space-y-2">
              <Label>Video file</Label>
              {file ? (
                <div className="rounded-lg border-2 bg-muted/20 p-4 shadow-sm">
                  <div className="flex items-start gap-3">
                    <div className="flex size-12 shrink-0 items-center justify-center rounded-lg border-2 border-primary/30 bg-primary/10">
                      <Video className="size-6 text-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold">{file.name}</p>
                      <p className="mt-1 text-xs font-medium text-muted-foreground">
                        {formatFileSize(file.size)} · {file.type || "video file"}
                      </p>
                      <div className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-primary">
                        <CheckCircle2 className="size-3.5" />
                        Ready to upload
                      </div>
                    </div>
                    <IconButton
                      label="Remove selected video"
                      icon={X}
                      variant="outline"
                      disabled={isUploading}
                      onClick={() => {
                        setFile(null);
                        setFileError(null);
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div
                  {...getRootProps()}
                  className={cn(
                    "group flex min-h-56 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-8 text-center transition",
                    isDragActive && !isDragReject && "border-primary bg-primary/5",
                    (isDragReject || fileError) && "border-destructive bg-destructive/5",
                    !isDragActive && !isDragReject && !fileError && "border-muted-foreground/35 hover:border-primary/60 hover:bg-muted/20",
                    isUploading && "pointer-events-none opacity-60"
                  )}
                >
                  <input {...getInputProps()} aria-label="Training video upload" />
                  <div className="flex size-14 items-center justify-center rounded-xl border-2 border-muted-foreground/20 bg-muted transition group-hover:border-primary/30">
                    {isDragReject ? (
                      <AlertCircle className="size-7 text-destructive" />
                    ) : (
                      <Upload className="size-7 text-muted-foreground group-hover:text-primary" />
                    )}
                  </div>
                  <p className="mt-4 text-base font-semibold">
                    {isDragActive ? "Drop the video here" : "Drop video here"}
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    or <span className="font-semibold text-primary">click to browse</span>
                  </p>
                  <p className="mt-3 text-xs font-medium text-muted-foreground">
                    MP4 or WebM files only
                  </p>
                </div>
              )}
              {fileError ? (
                <p className="flex items-center gap-1.5 text-sm font-semibold text-destructive">
                  <AlertCircle className="size-4" />
                  {fileError}
                </p>
              ) : null}
            </div>

            <div className="flex flex-col gap-4">
              <div className="space-y-2">
                <Label htmlFor="training-title">Title</Label>
                <Input
                  id="training-title"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  disabled={isUploading}
                  placeholder="e.g. Reviewing a generated submittal"
                  required
                />
                <p className="text-xs font-medium text-muted-foreground">
                  This title is visible to customers on the Training Videos page.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="training-description">Description</Label>
                <Textarea
                  id="training-description"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  disabled={isUploading}
                  placeholder="Briefly describe what users will learn."
                  className="min-h-32"
                />
              </div>

              <div className="mt-auto space-y-3 rounded-lg border-2 bg-muted/20 p-3">
                {uploadProgress !== null ? (
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      <span>Uploading to R2</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <Progress value={uploadProgress} />
                  </div>
                ) : null}
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-xs font-medium text-muted-foreground">
                    Uploads are published after the file finishes processing.
                  </p>
                  <Button type="submit" disabled={uploadDisabled} className="w-full sm:w-auto">
                    {isUploading ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />}
                    {isUploading ? `Uploading ${uploadProgress ?? 0}%` : "Upload and publish"}
                  </Button>
                </div>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card className="py-4">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Video className="size-4" />
            Library
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isPending ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="size-6 animate-spin text-muted-foreground" />
            </div>
          ) : videos.length === 0 ? (
            <div className="rounded-lg border-2 border-dashed p-8 text-center text-sm text-muted-foreground">
              No training videos have been uploaded yet.
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {videos.map((video) => (
                <div
                  key={video._id}
                  className="group flex flex-col rounded-lg border-2 bg-card shadow-sm transition hover:border-ring"
                >
                  <div className="relative flex aspect-video items-center justify-center bg-muted">
                    <Video className="size-8 text-muted-foreground" />
                    <IconButton
                      label={`Edit ${video.title}`}
                      icon={Edit3}
                      variant="secondary"
                      onClick={() => openEditDialog(video)}
                      className="absolute right-2 top-2 shadow-sm focus-visible:opacity-100 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100"
                    />
                    <div className="absolute bottom-2 left-2 flex items-center gap-2">
                      <StatusBadge
                        status={VIDEO_STATUS[video.status].status}
                        label={VIDEO_STATUS[video.status].label}
                      />
                      {video.isPublished ? (
                        <Badge variant="outline" size="sm" className="bg-background">
                          Published
                        </Badge>
                      ) : (
                        <Badge variant="secondary" size="sm">
                          Hidden
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-1 flex-col p-3">
                    <h3 className="text-sm font-semibold line-clamp-1">{video.title}</h3>
                    <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                      {video.description ?? "No description"}
                    </p>
                    <div className="mt-auto pt-2 text-xs text-muted-foreground">
                      {formatDuration(video.durationSeconds)}
                    </div>
                    {video.errorMessage ? (
                      <p className="mt-1 rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1 text-[11px] font-medium text-destructive">
                        {video.errorMessage}
                      </p>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!editingVideo} onOpenChange={(open) => { if (!open) closeEditDialog(); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Video</DialogTitle>
            <DialogDescription>Update the title, description, and visibility.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="edit-title">Title</Label>
              <Input
                id="edit-title"
                value={editTitle}
                onChange={(event) => setEditTitle(event.target.value)}
                disabled={isBusy}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={editDescription}
                onChange={(event) => setEditDescription(event.target.value)}
                disabled={isBusy}
                className="min-h-24"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="edit-published"
                checked={editPublished}
                disabled={isBusy}
                onCheckedChange={(checked) => setEditPublished(checked)}
              />
              <Label htmlFor="edit-published" className="cursor-pointer">
                {editPublished ? "Published" : "Hidden"}
              </Label>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" disabled={isBusy} onClick={closeEditDialog}>
              Cancel
            </Button>
            <Button variant="destructive" disabled={isBusy} onClick={() => void handleDelete()}>
              {isBusy ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4" />}
              Delete
            </Button>
            <Button disabled={isBusy} onClick={() => void handleSave()}>
              {isBusy ? <Loader2 className="size-4 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
